import { useState, useEffect, useCallback, useRef } from 'react';
import { settingsService } from '../services/settingsService';

const parseBool = (raw: unknown): boolean | null => {
  if (raw === null || raw === undefined) return null;
  const val = raw.toString().trim().toLowerCase();
  if (val === 'true' || val === '1' || val === 'ja') return true;
  if (val === 'false' || val === '0' || val === 'nei') return false;
  return null;
};

/**
 * Leser og lagrer en bool-innstilling per operatør (ProfileSetting).
 *
 * Lesing følger hierarkiet Profile → System → Default via getEffectiveSetting, som
 * useShowTransferToLocationSuggestions. Lagring skriver alltid på operatørens profil
 * (krever SettingDefinition med AllowProfileLevel=1 i master).
 *
 * setValue oppdaterer optimistisk og ruller tilbake hvis lagringen feiler; returnerer
 * true når verdien er lagret.
 *
 * Brukt av fritelling (TW 36413027 «Hurtigskanning»).
 */
export function useProfileBoolSetting(settingKey: string, defaultValue: boolean) {
  const [value, setValueState] = useState<boolean>(defaultValue);
  const [loaded, setLoaded] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    let cancelled = false;
    settingsService.getEffectiveSetting(settingKey)
      .then(setting => {
        if (cancelled) return;
        const parsed = parseBool(setting?.currentValue);
        setValueState(parsed ?? defaultValue);
      })
      .catch(err => {
        console.warn(`Could not load ${settingKey} setting, defaulting to ${defaultValue}:`, err);
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [settingKey, defaultValue]);

  const setValue = useCallback(async (next: boolean): Promise<boolean> => {
    const previous = valueRef.current;
    setValueState(next);
    try {
      await settingsService.setProfileSetting(settingKey, next ? 'true' : 'false');
      return true;
    } catch (err) {
      console.error(`Could not save ${settingKey} setting:`, err);
      setValueState(previous);
      return false;
    }
  }, [settingKey]);

  return { value, setValue, loaded };
}
